/**
 * SIL Engine - Safety Integrity Level Evaluation
 * OAV-039: SIL assessment per IEC 61508 / IEC 61511
 *
 * REFERENCES:
 *   - IEC 61508-1:2010 Functional safety of electrical/electronic/programmable electronic safety-related systems
 *   - IEC 61508-2:2010 Requirements for electrical/e/pe safety-related systems
 *   - IEC 61508-4:2010 Definitions and abbreviations
 *   - IEC 61508-5:2010 Examples of methods for the determination of safety integrity levels
 *   - IEC 61508-6:2010 Functional safety assessment
 *   - IEC 61508-7:2010 Overview and guidance for the functional safety standards
 *   - IEC 61511-1:2016 Functional safety - Safety instrumented systems for the process industry sector
 *   - IEC 61511-2:2016 Guidelines on the application of IEC 61511-1
 *   - IEC 61511-3:2016 Guidance on determination of the required Safety Integrity Level (SIL)
 *
 * OVERVIEW:
 *   This module provides methods for Safety Integrity Level (SIL) evaluation in low-demand
 *   and high-demand modes, architectural constraint checking, and Layer of Protection
 *   Analysis (LOPA). SIL bands (1-4) are defined per IEC 61508-6 Annex B.
 */

class SILEngine {
  /**
   * Compute average Probability of Failure on Demand (PFD_avg)
   * for low-demand mode per IEC 61508-6 Annex B
   *
   * @param {number} lambda_du - Detected undetected failure rate (failures/hour)
   * @param {number} ti - Test interval (hours)
   * @param {number} mttr - Mean time to repair (hours)
   * @param {string} voting - Voting architecture: '1oo1', '1oo2', '2oo3'
   * @returns {number} PFD_avg - probability of failure on demand
   *
   * FORMULAS (IEC 61508-6:2010):
   *   1oo1: PFD_avg = (λ_du · TI / 2) + (λ_du · MTTR)
   *   1oo2: PFD_avg = ((λ_du · TI)^2 / 3) + (2 · λ_du · MTTR)
   *   2oo3: PFD_avg = (2 · (λ_du · TI)^2 / 3) + (3 · λ_du · MTTR)
   */
  pfdAvg(lambda_du, ti, mttr, voting = '1oo1') {
    if (lambda_du < 0 || ti < 0 || mttr < 0) {
      throw new Error('lambda_du, ti, mttr must be non-negative');
    }

    const term1 = lambda_du * mttr;

    switch (voting) {
      case '1oo1':
        // PFD = (λ_du · TI / 2) + (λ_du · MTTR)
        return (lambda_du * ti) / 2 + term1;

      case '1oo2':
        // PFD = ((λ_du · TI)^2 / 3) + (2 · λ_du · MTTR)
        const term1oo2 = Math.pow(lambda_du * ti, 2) / 3;
        return term1oo2 + 2 * term1;

      case '2oo3':
        // PFD = (2 · (λ_du · TI)^2 / 3) + (3 · λ_du · MTTR)
        const term2oo3 = (2 * Math.pow(lambda_du * ti, 2)) / 3;
        return term2oo3 + 3 * term1;

      default:
        throw new Error('Voting must be one of: 1oo1, 1oo2, 2oo3');
    }
  }

  /**
   * Compute Probability of Failure per Hour (PFH)
   * for high-demand mode per IEC 61508-6:2010
   *
   * @param {number} lambda_du - Detected undetected failure rate (failures/hour)
   * @param {string} voting - Voting architecture: '1oo1', '1oo2', '2oo3'
   * @param {number} ti - Test interval (hours) - required for 1oo2 and 2oo3
   * @returns {number} PFH - probability of failure per hour
   *
   * FORMULAS (IEC 61508-6:2010):
   *   1oo1: PFH = λ_du
   *   1oo2: PFH = 2 · λ_du^2 · TI
   *   2oo3: PFH = 6 · λ_du^2 · TI
   */
  pfh(lambda_du, voting = '1oo1', ti = 1) {
    if (lambda_du < 0 || ti < 0) {
      throw new Error('lambda_du and ti must be non-negative');
    }

    switch (voting) {
      case '1oo1':
        return lambda_du;

      case '1oo2':
        // PFH = 2 · λ_du^2 · TI
        return 2 * Math.pow(lambda_du, 2) * ti;

      case '2oo3':
        // PFH = 6 · λ_du^2 · TI
        return 6 * Math.pow(lambda_du, 2) * ti;

      default:
        throw new Error('Voting must be one of: 1oo1, 1oo2, 2oo3');
    }
  }

  /**
   * Map PFD value to SIL band (low-demand mode)
   * per IEC 61508-6 Annex B
   *
   * @param {number} pfd - Probability of Failure on Demand
   * @returns {object} {sil: (1|2|3|4|null), range: [min, max]}
   *
   * SIL RANGES (PFD):
   *   SIL 4: 1e-5 ≤ PFD < 1e-4
   *   SIL 3: 1e-4 ≤ PFD < 1e-3
   *   SIL 2: 1e-3 ≤ PFD < 1e-2
   *   SIL 1: 1e-2 ≤ PFD < 1e-1
   *   Below SIL 1: PFD ≥ 1e-1
   */
  silFromPfd(pfd) {
    if (pfd < 0) {
      throw new Error('PFD must be non-negative');
    }

    if (pfd >= 1e-5 && pfd < 1e-4) {
      return { sil: 4, range: [1e-5, 1e-4] };
    }
    if (pfd >= 1e-4 && pfd < 1e-3) {
      return { sil: 3, range: [1e-4, 1e-3] };
    }
    if (pfd >= 1e-3 && pfd < 1e-2) {
      return { sil: 2, range: [1e-3, 1e-2] };
    }
    if (pfd >= 1e-2 && pfd < 1e-1) {
      return { sil: 1, range: [1e-2, 1e-1] };
    }
    if (pfd >= 1e-1) {
      return { sil: null, range: null, note: 'Below SIL 1' };
    }
    if (pfd < 1e-5) {
      return { sil: null, range: null, note: 'Exceeds SIL 4 (overly conservative)' };
    }
  }

  /**
   * Map PFH value to SIL band (high-demand mode)
   * per IEC 61508-6:2010 Annex B
   *
   * @param {number} pfh - Probability of Failure per Hour
   * @returns {object} {sil: (1|2|3|4|null), range: [min, max]}
   *
   * SIL RANGES (PFH):
   *   SIL 4: 1e-9 ≤ PFH < 1e-8
   *   SIL 3: 1e-8 ≤ PFH < 1e-7
   *   SIL 2: 1e-7 ≤ PFH < 1e-6
   *   SIL 1: 1e-6 ≤ PFH < 1e-5
   *   Below SIL 1: PFH ≥ 1e-5
   */
  silFromPfh(pfh) {
    if (pfh < 0) {
      throw new Error('PFH must be non-negative');
    }

    if (pfh >= 1e-9 && pfh < 1e-8) {
      return { sil: 4, range: [1e-9, 1e-8] };
    }
    if (pfh >= 1e-8 && pfh < 1e-7) {
      return { sil: 3, range: [1e-8, 1e-7] };
    }
    if (pfh >= 1e-7 && pfh < 1e-6) {
      return { sil: 2, range: [1e-7, 1e-6] };
    }
    if (pfh >= 1e-6 && pfh < 1e-5) {
      return { sil: 1, range: [1e-6, 1e-5] };
    }
    if (pfh >= 1e-5) {
      return { sil: null, range: null, note: 'Below SIL 1' };
    }
    if (pfh < 1e-9) {
      return { sil: null, range: null, note: 'Exceeds SIL 4' };
    }
  }

  /**
   * Determine max allowable SIL per IEC 61508-2 Table 2 (type A) & Table 3 (type B)
   * based on Safe Failure Fraction (SFF) and Hardware Fault Tolerance (HFT)
   *
   * @param {number} sff - Safe Failure Fraction (0 ≤ SFF ≤ 1)
   * @param {number} hft - Hardware Fault Tolerance (typically 0, 1, 2, ...)
   * @param {string} type - Architecture type: 'A' (simpler) or 'B' (more complex)
   * @returns {object} {maxSil, table, constraints}
   *
   * TYPICAL RULES (IEC 61508-2):
   *   Type A (e.g., single-channel, single element):
   *     HFT=0, SFF<60%: max SIL 1
   *     HFT=0, 60%≤SFF<90%: max SIL 2
   *     HFT=0, SFF≥90%: max SIL 3
   *     HFT=1, SFF≥60%: max SIL 3
   *     HFT≥2, SFF≥60%: max SIL 4
   *
   *   Type B (e.g., complex electronics):
   *     HFT=0, SFF<90%: max SIL 2
   *     HFT=0, SFF≥90%: max SIL 3
   *     HFT=1, SFF≥90%: max SIL 4
   */
  architecturalConstraints(sff, hft, type = 'A') {
    if (sff < 0 || sff > 1) {
      throw new Error('SFF must be in range [0, 1]');
    }
    if (hft < 0) {
      throw new Error('HFT must be non-negative');
    }
    if (!['A', 'B'].includes(type)) {
      throw new Error('Type must be A or B');
    }

    let maxSil, constraints;

    if (type === 'A') {
      // Type A (simple, single-channel)
      if (hft === 0) {
        if (sff < 0.6) {
          maxSil = 1;
          constraints = 'HFT=0, SFF<60% → max SIL 1';
        } else if (sff < 0.9) {
          maxSil = 2;
          constraints = 'HFT=0, 60%≤SFF<90% → max SIL 2';
        } else {
          maxSil = 3;
          constraints = 'HFT=0, SFF≥90% → max SIL 3';
        }
      } else if (hft === 1) {
        if (sff >= 0.6) {
          maxSil = 3;
          constraints = 'HFT=1, SFF≥60% → max SIL 3';
        } else {
          maxSil = 1;
          constraints = 'HFT=1, SFF<60% → max SIL 1';
        }
      } else {
        // HFT ≥ 2
        if (sff >= 0.6) {
          maxSil = 4;
          constraints = 'HFT≥2, SFF≥60% → max SIL 4';
        } else {
          maxSil = 2;
          constraints = 'HFT≥2, SFF<60% → max SIL 2';
        }
      }
    } else {
      // Type B (complex electronics)
      if (hft === 0) {
        if (sff >= 0.9) {
          maxSil = 3;
          constraints = 'Type B, HFT=0, SFF≥90% → max SIL 3';
        } else {
          maxSil = 2;
          constraints = 'Type B, HFT=0, SFF<90% → max SIL 2';
        }
      } else if (hft === 1) {
        if (sff >= 0.9) {
          maxSil = 4;
          constraints = 'Type B, HFT=1, SFF≥90% → max SIL 4';
        } else {
          maxSil = 3;
          constraints = 'Type B, HFT=1, SFF<90% → max SIL 3';
        }
      } else {
        // HFT ≥ 2: Type B allows SIL 4 with lower SFF requirement
        maxSil = 4;
        constraints = 'Type B, HFT≥2 → max SIL 4';
      }
    }

    return {
      maxSil,
      table: `IEC 61508-2 Table ${type === 'A' ? '2' : '3'}`,
      constraints,
      sff: Math.round(sff * 100) + '%',
      hft
    };
  }

  /**
   * Compute Risk Reduction Factor (RRF) for a target SIL
   * RRF = 1 / PFD_target or 1 / PFH_target (depending on mode)
   *
   * @param {number} targetSil - Target SIL (1-4)
   * @param {string} mode - 'low-demand' or 'high-demand'
   * @returns {object} {rrf: number, range: [min, max], description}
   *
   * TYPICAL RRF BY SIL (low-demand mode):
   *   SIL 1: RRF = 10–100 (PFD 1e-2 to 1e-1)
   *   SIL 2: RRF = 100–1,000 (PFD 1e-3 to 1e-2)
   *   SIL 3: RRF = 1,000–10,000 (PFD 1e-4 to 1e-3)
   *   SIL 4: RRF = 10,000–100,000 (PFD 1e-5 to 1e-4)
   */
  riskReductionFactor(targetSil, mode = 'low-demand') {
    const silRanges = {
      'low-demand': {
        1: { min: 1e-2, max: 1e-1 },
        2: { min: 1e-3, max: 1e-2 },
        3: { min: 1e-4, max: 1e-3 },
        4: { min: 1e-5, max: 1e-4 }
      },
      'high-demand': {
        1: { min: 1e-6, max: 1e-5 },
        2: { min: 1e-7, max: 1e-6 },
        3: { min: 1e-8, max: 1e-7 },
        4: { min: 1e-9, max: 1e-8 }
      }
    };

    if (!silRanges[mode]) {
      throw new Error("Mode must be 'low-demand' or 'high-demand'");
    }
    if (![1, 2, 3, 4].includes(targetSil)) {
      throw new Error('Target SIL must be 1-4');
    }

    const range = silRanges[mode][targetSil];
    const rrf_min = 1 / range.max;
    const rrf_max = 1 / range.min;

    return {
      sil: targetSil,
      mode,
      rrfMin: Math.round(rrf_min),
      rrfMax: Math.round(rrf_max),
      range: `${Math.round(rrf_min)} – ${Math.round(rrf_max)}`,
      description: `Risk must be reduced by at least 1 in ${Math.round(rrf_min)} through protection layers`
    };
  }

  /**
   * Layer of Protection Analysis (LOPA)
   * Compute residual risk and compare to target SIL
   *
   * @param {object} params
   *   - initiatingFreq: Initiating event frequency (events/year)
   *   - ipls: Array of Independent Protection Layers, each {name, pfd}
   *   - targetSil: Target SIL (1-4)
   * @returns {object} Analysis results including residual frequency
   *
   * FORMULA (IEC 61511):
   *   Residual Freq = Initiating Freq × ∏(1 − IPL_PFD)
   *   Compare residual to acceptable risk target derived from target SIL
   */
  lopaCalculation({ initiatingFreq, ipls, targetSil }) {
    if (initiatingFreq <= 0) {
      throw new Error('initiatingFreq must be positive');
    }
    if (!Array.isArray(ipls) || ipls.length === 0) {
      throw new Error('ipls must be a non-empty array');
    }
    if (![1, 2, 3, 4].includes(targetSil)) {
      throw new Error('targetSil must be 1-4');
    }

    // Compute product of (1 - pfd) across all IPLs
    let productTerm = 1;
    const iplResults = [];

    for (const ipl of ipls) {
      if (ipl.pfd < 0 || ipl.pfd > 1) {
        throw new Error(`IPL ${ipl.name}: PFD must be in [0, 1]`);
      }
      const term = 1 - ipl.pfd;
      productTerm *= term;
      iplResults.push({
        name: ipl.name,
        pfd: ipl.pfd,
        reduction: term
      });
    }

    const residualFreq = initiatingFreq * productTerm;

    // Target acceptable frequency per SIL (low-demand assumption)
    // Derived from PFD: acceptable_freq = 1 / (100 years) ≈ 1.14e-5 /year for SIL 1
    const silTargets = {
      1: 1e-4,  // 1 in 10,000 years
      2: 1e-5,  // 1 in 100,000 years
      3: 1e-6,  // 1 in 1,000,000 years
      4: 1e-7   // 1 in 10,000,000 years
    };

    const acceptableFreq = silTargets[targetSil];
    const isAchieved = residualFreq <= acceptableFreq;

    return {
      initiatingFreq,
      targetSil,
      ipls: iplResults,
      productReduction: productTerm,
      residualFreq,
      acceptableFreq,
      achieved: isAchieved,
      summary: isAchieved
        ? `Residual risk ${residualFreq.toExponential(2)}/year meets target SIL ${targetSil}`
        : `Residual risk ${residualFreq.toExponential(2)}/year exceeds target SIL ${targetSil}`
    };
  }
}

module.exports = SILEngine;
